import { useMemo, useCallback, useEffect } from "react";
import isEqual from "lodash/isEqual";
import { ComparablesFilterOptions } from "react-migration/domains/comparables/typings/Comparables";
import { useLocalStorageReducer } from "react-migration/lib/persistence/state/useLocalStorageReducer";
import { useFiltersUpdate } from "react-migration/domains/comparables/components/Filters/FiltersWithClear";
import {
  getDefaultHmlrFilters,
  defaultHometrackFilters,
  getDefaultFilters,
} from "react-migration/domains/comparables/util/defaultFilters";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";

type Action = { type: "SET_FILTERS"; value: ComparablesFilterOptions } | { type: "CLEAR_STATE" };

function createReducer(defaultState: ComparablesFilterOptions) {
  return function reducer(state: ComparablesFilterOptions, action: Action): ComparablesFilterOptions {
    switch (action.type) {
      case "SET_FILTERS":
        return { ...state, ...action.value } as ComparablesFilterOptions;
      case "CLEAR_STATE":
        return { ...defaultState };
    }
  };
}

export type ComparablesFilterState = ReturnType<typeof useComparablesFilterState>[0];
export type ComparablesFilterStateDispatch = ReturnType<typeof useComparablesFilterState>[1];

export function useComparablesFilterState(
  localStorageKey: string,
  initialStatePartial: Partial<ComparablesFilterOptions> = {},
  dataSource: "hmlr" | "hometrack",
  eventTracking?: (values: { filterName: string; filterState: unknown }) => void
) {
  const hometrackv2 = hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess);

  const defaultState = useMemo(() => {
    if (!hometrackv2) {
      return {
        ...structuredClone({ ...getDefaultFilters(), dateFrom: null, dateTo: null }),
        ...initialStatePartial,
      } as ComparablesFilterOptions;
    }

    return {
      ...structuredClone({
        ...(dataSource === "hmlr" ? getDefaultHmlrFilters() : defaultHometrackFilters),
        dateFrom: null,
        dateTo: null,
      }),
      ...initialStatePartial,
    } as ComparablesFilterOptions;
  }, [initialStatePartial, dataSource, hometrackv2]);

  const [state, dispatch] = useLocalStorageReducer(localStorageKey, createReducer(defaultState), defaultState);

  useEffect(() => {
    dispatch({ type: "SET_FILTERS", value: defaultState });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, dispatch]);

  const hasFilters = useMemo(() => !isEqual(defaultState, state), [defaultState, state]);

  const hasNoFilters = useMemo(
    () =>
      isEqual(
        { ...defaultState, dcsConditions: defaultState?.dcsConditions?.length },
        { ...state, dcsConditions: state?.dcsConditions?.length }
      ),
    [defaultState, state]
  );

  const selectedDCSConditions = state.dcsConditions;

  const setFilters = useCallback(
    (filters: ComparablesFilterOptions) => {
      dispatch({ type: "SET_FILTERS", value: filters });
    },
    [dispatch]
  );

  const updateFilters = useFiltersUpdate(state, setFilters, selectedDCSConditions!);

  const trackingUpdateFilters = useCallback(
    (val: Partial<ComparablesFilterOptions>) => {
      if (eventTracking) {
        for (const [key, value] of Object.entries(val)) {
          eventTracking({ filterName: key, filterState: value });
        }
      }

      updateFilters(val);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventTracking, updateFilters, state]
  );

  const clearFilters = useCallback(() => {
    // Track the filter changes if eventTracking is provided
    if (eventTracking) {
      for (const [key, value] of Object.entries(defaultState)) {
        eventTracking({ filterName: key, filterState: value });
      }
    }

    dispatch({ type: "CLEAR_STATE" });
  }, [eventTracking, defaultState, dispatch]);

  return [
    state,
    {
      hasFilters,
      hasNoFilters,
      updateFilters,
      trackingUpdateFilters,
      clearFilters,
      selectedDCSConditions,
    },
  ] as const;
}
